/**
 * Cookie-consent storage + the Google Consent Mode mapping.
 *
 * Browser-only reads/writes are guarded; the mapping functions are pure and
 * unit-tested (tests/unit/cookie-consent.test.ts).
 *
 * Stored in localStorage under `cookie-consent` as JSON. `version` and
 * `updatedAt` were added 2026-09; older entries without them stay valid (the
 * categories did not change).
 */

export type CookieConsent = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  /** Consent-text version the choice was made under. */
  version?: number;
  /** ISO timestamp of the choice. */
  updatedAt?: string;
};

export const CONSENT_KEY = 'cookie-consent';
export const CONSENT_VERSION = 1;
export const CONSENT_CHANGED_EVENT = 'cookie-consent-changed';
export const OPEN_COOKIE_SETTINGS_EVENT = 'open-cookie-settings';

export function getStoredConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ...parsed,
      essential: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
    };
  } catch {
    return null;
  }
}

export function storeConsent(choice: { analytics: boolean; marketing: boolean }): CookieConsent {
  const consent: CookieConsent = {
    essential: true,
    analytics: choice.analytics,
    marketing: choice.marketing,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // Storage blocked (private mode etc.) — the choice still applies to this page.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: consent }));
  return consent;
}

/** Re-open the consent banner in its detailed view (footer link, cookies page). */
export function openCookieSettings(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}

export type GoogleConsentValue = 'granted' | 'denied';

export interface GoogleConsentState {
  analytics_storage: GoogleConsentValue;
  ad_storage: GoogleConsentValue;
  ad_user_data: GoogleConsentValue;
  ad_personalization: GoogleConsentValue;
}

/** Consent Mode v2 default: everything denied until the stored choice says otherwise. */
export const GOOGLE_CONSENT_DENIED: GoogleConsentState = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

/**
 * Map the banner's two categories onto Consent Mode v2. "Analytics" covers
 * GA4 measurement only; every advertising signal (ad cookies, sending user
 * data to Google Ads, remarketing/personalisation) follows "Marketing".
 */
export function toGoogleConsent(consent: Pick<CookieConsent, 'analytics' | 'marketing'> | null): GoogleConsentState {
  const g = (on: boolean | undefined): GoogleConsentValue => (on ? 'granted' : 'denied');
  return {
    analytics_storage: g(consent?.analytics),
    ad_storage: g(consent?.marketing),
    ad_user_data: g(consent?.marketing),
    ad_personalization: g(consent?.marketing),
  };
}

/** True when `next` withdraws a category `prev` had granted. */
export function isConsentWithdrawn(
  prev: Pick<CookieConsent, 'analytics' | 'marketing'> | null,
  next: Pick<CookieConsent, 'analytics' | 'marketing'> | null,
): boolean {
  if (!prev) return false;
  return (prev.analytics && !next?.analytics) || (prev.marketing && !next?.marketing);
}
